/**
 * Mode Registry and Detection
 * Mode configs power dashboard navigation, transcript tabs, and GTM handoff.
 */

'use client';

import { ModeConfig } from './config';
import { creatorMode } from './creator';
import { defaultMode } from './default';
import { salesMode } from './sales';

// Mode registry (app behavior). Do not add entries without implementing sidebar/tab config.
const MODES: Record<string, ModeConfig> = {
  default: defaultMode,
  sales: salesMode,
  creator: creatorMode,
};

/**
 * Detect current mode from URL pathname
 * Priority: URL path > query param > localStorage > default
 */
export function detectModeFromPath(pathname: string): string {
  // Check if pathname starts with mode-specific routes
  if (pathname.startsWith('/sales')) {
    return 'sales';
  }

  if (pathname.startsWith('/creators')) {
    return 'creator';
  }
  
  // Default mode for all other routes
  return 'default';
}

/**
 * Get current mode from various sources
 * @param pathname - Current URL pathname
 * @param searchParams - URL search params (optional)
 * @returns Mode ID
 */
export function getCurrentMode(
  pathname: string,
  searchParams?: URLSearchParams
): string {
  // 1. Check query parameter (for testing/debugging)
  if (searchParams?.get('mode')) {
    const queryMode = searchParams.get('mode');

    if (queryMode && MODES[queryMode]) {
      return queryMode;
    }
  }

  // 2. For dashboard routes, prioritize localStorage preference
  // For marketing/landing pages, use URL path detection
  const isDashboardRoute = pathname.startsWith('/dashboard');
  
  if (isDashboardRoute && typeof window !== 'undefined') {
    // Check localStorage for user preference first (for dashboard)
    const storedMode = localStorage.getItem('wisprs_mode');

    if (storedMode && MODES[storedMode]) {
      // Verify mode is enabled in preferences
      const prefs = localStorage.getItem('wisprs_mode_preferences');

      if (prefs) {
        try {
          const preferences = JSON.parse(prefs);

          if (preferences[storedMode] === true) {
            return storedMode;
          }
        } catch (e) {
          // If preferences are invalid, use stored mode anyway
          return storedMode;
        }
      } else {
        return storedMode;
      }
    }
    
    // Check preferences for enabled modes
    const prefs = localStorage.getItem('wisprs_mode_preferences');

    if (prefs) {
      try {
        const preferences = JSON.parse(prefs);

        const enabledModes = Object.entries(preferences)
          .filter(([_, enabled]) => enabled)
          .map(([modeId]) => modeId);
        
        if (enabledModes.length > 0) {
          // Return first enabled mode (or default if available)
          return enabledModes.includes('default') ? 'default' : enabledModes[0];
        }
      } catch (e) {
        // Invalid preferences, fall through
      }
    }
  }

  // 3. Check URL path (for marketing/landing pages)
  const pathMode = detectModeFromPath(pathname);

  if (pathMode && MODES[pathMode]) {
    return pathMode;
  }

  // 4. Fallback to default
  return 'default';
}

/**
 * Get mode configuration by ID
 * @param modeId - Mode identifier
 * @returns Mode configuration or default mode if not found
 */
export function getModeConfig(modeId: string): ModeConfig {
  return MODES[modeId] || MODES.default;
}

/**
 * Check if a specific mode is currently active
 * @param modeId - Mode identifier to check
 * @param pathname - Current URL pathname
 * @param searchParams - URL search params (optional)
 * @returns True if mode is active
 */
export function isModeActive(
  modeId: string,
  pathname: string,
  searchParams?: URLSearchParams
): boolean {
  return getCurrentMode(pathname, searchParams) === modeId;
}

/**
 * Register a new mode (for future extensibility)
 * @param modeId - Mode identifier
 * @param config - Mode configuration
 */
export function registerMode(modeId: string, config: ModeConfig): void {
  MODES[modeId] = config;
}

/**
 * Get all available modes
 * @returns Array of mode IDs
 */
export function getAvailableModes(): string[] {
  return Object.keys(MODES);
}
